import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import posthog from 'posthog-js';
import { useFirebaseAuth } from './useFirebaseAuth';
import { RootState } from 'store/rootReducers';
import { selectProfile } from 'store/selectors/profile';

/**
 * PostHog User Identification Hook
 *
 * Identifies users in PostHog when they are authenticated.
 * Uses Firebase UID as the distinct_id to maintain consistent
 * user identity across audit-admin and admin-lite.
 *
 * User properties include:
 * - email: User's email address
 * - role: User's role in the organization
 * - organizationId: User's organization ID
 * - platform: 'audit-admin' to distinguish from admin-lite
 */
export function usePostHogIdentify() {
	const { user } = useFirebaseAuth();
	const profile = useSelector(selectProfile);
	const identifiedRef = useRef<string | null>(null);
	const [isIdentified, setIsIdentified] = useState(false);

	useEffect(() => {
		// Only identify if PostHog is initialized
		if (!import.meta.env.REACT_APP_POSTHOG_KEY) {
			return;
		}

		if (user?.uid && profile && !profile.isEmpty) {
			// Only identify if we haven't already for this user
			if (identifiedRef.current !== user.uid) {
				posthog.identify(user.uid, {
					email: user.email || profile.email,
					name: profile.name,
					role: profile.role,
					organizationId: profile.organization,
					environment: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
					platform: 'audit-admin',
				});
				identifiedRef.current = user.uid;
				setIsIdentified(true);
			}
		} else if (!user && identifiedRef.current) {
			// Reset on logout
			posthog.reset();
			identifiedRef.current = null;
			setIsIdentified(false);
		}
	}, [user, profile]);

	return { isIdentified };
}
